import { z } from 'zod';
// Activity Types
export var ActivityType;
(function (ActivityType) {
    // Lead Activities
    ActivityType["LEAD_CREATED"] = "LEAD_CREATED";
    ActivityType["LEAD_UPDATED"] = "LEAD_UPDATED";
    ActivityType["LEAD_STATUS_CHANGED"] = "LEAD_STATUS_CHANGED";
    ActivityType["LEAD_ASSIGNED"] = "LEAD_ASSIGNED";
    ActivityType["LEAD_UNASSIGNED"] = "LEAD_UNASSIGNED";
    ActivityType["LEAD_DELETED"] = "LEAD_DELETED";
    // Task Activities
    ActivityType["TASK_CREATED"] = "TASK_CREATED";
    ActivityType["TASK_UPDATED"] = "TASK_UPDATED";
    ActivityType["TASK_STARTED"] = "TASK_STARTED";
    ActivityType["TASK_COMPLETED"] = "TASK_COMPLETED";
    ActivityType["TASK_CANCELLED"] = "TASK_CANCELLED";
    ActivityType["TASK_ASSIGNED"] = "TASK_ASSIGNED";
    ActivityType["TASK_STATUS_CHANGED"] = "TASK_STATUS_CHANGED";
    ActivityType["TASK_PRIORITY_CHANGED"] = "TASK_PRIORITY_CHANGED";
    ActivityType["TASK_DUE_DATE_CHANGED"] = "TASK_DUE_DATE_CHANGED";
    // Communication Activities
    ActivityType["CALL_MADE"] = "CALL_MADE";
    ActivityType["CALL_RECEIVED"] = "CALL_RECEIVED";
    ActivityType["EMAIL_SENT"] = "EMAIL_SENT";
    ActivityType["EMAIL_RECEIVED"] = "EMAIL_RECEIVED";
    ActivityType["SMS_SENT"] = "SMS_SENT";
    ActivityType["WHATSAPP_SENT"] = "WHATSAPP_SENT";
    // Meeting Activities
    ActivityType["MEETING_SCHEDULED"] = "MEETING_SCHEDULED";
    ActivityType["MEETING_COMPLETED"] = "MEETING_COMPLETED";
    ActivityType["MEETING_CANCELLED"] = "MEETING_CANCELLED";
    ActivityType["MEETING_RESCHEDULED"] = "MEETING_RESCHEDULED";
    // Document Activities
    ActivityType["DOCUMENT_UPLOADED"] = "DOCUMENT_UPLOADED";
    ActivityType["DOCUMENT_DOWNLOADED"] = "DOCUMENT_DOWNLOADED";
    ActivityType["DOCUMENT_SHARED"] = "DOCUMENT_SHARED";
    // Note Activities
    ActivityType["NOTE_ADDED"] = "NOTE_ADDED";
    ActivityType["NOTE_UPDATED"] = "NOTE_UPDATED";
    ActivityType["NOTE_DELETED"] = "NOTE_DELETED";
    // Follow-up Activities
    ActivityType["FOLLOW_UP_SCHEDULED"] = "FOLLOW_UP_SCHEDULED";
    ActivityType["FOLLOW_UP_COMPLETED"] = "FOLLOW_UP_COMPLETED";
    ActivityType["REMINDER_SET"] = "REMINDER_SET";
    // System Activities
    ActivityType["BULK_IMPORT"] = "BULK_IMPORT";
    ActivityType["EXPORT_GENERATED"] = "EXPORT_GENERATED";
    ActivityType["INTEGRATION_SYNC"] = "INTEGRATION_SYNC";
})(ActivityType || (ActivityType = {}));
// Base Activity Schema
export const BaseActivitySchema = z.object({
    id: z.string().uuid(),
    type: z.nativeEnum(ActivityType),
    title: z.string().min(1).max(200),
    description: z.string().optional(),
    // Context
    tenantId: z.string().uuid(),
    leadId: z.string().uuid().optional(),
    taskId: z.string().uuid().optional(),
    userId: z.string().uuid().optional(),
    // Activity metadata
    metadata: z.record(z.string(), z.any()).default({}),
    // Tracking
    createdBy: z.string().uuid(),
    createdAt: z.date(),
    // For updates/changes
    oldValue: z.any().optional(),
    newValue: z.any().optional(),
    // Additional context
    ipAddress: z.string().optional(),
    userAgent: z.string().optional(),
    source: z.enum(['WEB', 'MOBILE', 'API', 'SYSTEM']).default('WEB'),
});
// Lead Activity Schema
export const LeadActivitySchema = BaseActivitySchema.extend({
    leadId: z.string().uuid(),
});
// Task Activity Schema
export const TaskActivitySchema = BaseActivitySchema.extend({
    taskId: z.string().uuid(),
});
// Communication Activity Schema
export const CommunicationActivitySchema = BaseActivitySchema.extend({
    leadId: z.string().uuid().optional(),
    taskId: z.string().uuid().optional(),
    // Communication specific fields
    communicationType: z.enum(['CALL', 'EMAIL', 'SMS', 'WHATSAPP', 'MEETING']),
    direction: z.enum(['INBOUND', 'OUTBOUND']).optional(),
    duration: z.number().optional(), // in seconds
    outcome: z.enum(['SUCCESSFUL', 'NO_ANSWER', 'BUSY', 'FAILED', 'SCHEDULED']).optional(),
    // Contact details
    contactMethod: z.string().optional(), // phone number, email, etc.
    subject: z.string().optional(),
    content: z.string().optional(),
    // Meeting specific
    meetingDate: z.date().optional(),
    meetingLocation: z.string().optional(),
    attendees: z.array(z.string()).default([]),
    // Call specific
    callStartTime: z.date().optional(),
    callEndTime: z.date().optional(),
    recordingUrl: z.string().optional(),
});
// Document Activity Schema
export const DocumentActivitySchema = BaseActivitySchema.extend({
    leadId: z.string().uuid().optional(),
    taskId: z.string().uuid().optional(),
    // Document details
    documentId: z.string().uuid(),
    documentName: z.string(),
    documentType: z.string(),
    documentSize: z.number().optional(),
    documentUrl: z.string().optional(),
    // Access details
    sharedWith: z.array(z.string()).default([]),
    accessLevel: z.enum(['VIEW', 'EDIT', 'DOWNLOAD']).optional(),
});
// Bulk Activity Schema for batch operations
export const BulkActivitySchema = z.object({
    activities: z.array(BaseActivitySchema),
    batchId: z.string().uuid(),
    totalCount: z.number(),
    successCount: z.number(),
    failureCount: z.number(),
    errors: z.array(z.object({
        activityIndex: z.number(),
        error: z.string(),
    })).default([]),
});
// Activity Filter Schema
export const ActivityFilterSchema = z.object({
    leadId: z.string().uuid().optional(),
    taskId: z.string().uuid().optional(),
    userId: z.string().uuid().optional(),
    types: z.array(z.nativeEnum(ActivityType)).optional(),
    dateFrom: z.date().optional(),
    dateTo: z.date().optional(),
    createdBy: z.array(z.string().uuid()).optional(),
    source: z.array(z.enum(['WEB', 'MOBILE', 'API', 'SYSTEM'])).optional(),
    search: z.string().optional(),
    limit: z.number().min(1).max(1000).default(50),
    offset: z.number().min(0).default(0),
});
// Activity Summary Schema
export const ActivitySummarySchema = z.object({
    leadId: z.string().uuid().optional(),
    taskId: z.string().uuid().optional(),
    totalActivities: z.number(),
    activitiesByType: z.record(z.string(), z.number()),
    activitiesByUser: z.record(z.string(), z.number()),
    activitiesByDate: z.record(z.string(), z.number()),
    lastActivity: BaseActivitySchema.optional(),
    mostActiveUser: z.string().optional(),
    mostCommonType: z.string().optional(),
    // Communication summary
    totalCalls: z.number(),
    totalEmails: z.number(),
    totalMeetings: z.number(),
    averageCallDuration: z.number().optional(),
    // Task summary (if applicable)
    tasksCreated: z.number().optional(),
    tasksCompleted: z.number().optional(),
    taskCompletionRate: z.number().optional(),
});
// Activity Factory
export class ActivityFactory {
    static createLeadActivity(leadId, type, title, createdBy, options) {
        return LeadActivitySchema.parse({
            id: crypto.randomUUID(),
            type,
            title,
            leadId,
            createdBy,
            createdAt: new Date(),
            ...options,
        });
    }
    static createTaskActivity(taskId, type, title, createdBy, options) {
        return TaskActivitySchema.parse({
            id: crypto.randomUUID(),
            type,
            title,
            taskId,
            createdBy,
            createdAt: new Date(),
            ...options,
        });
    }
    static createCommunicationActivity(type, title, createdBy, communicationType, options) {
        return CommunicationActivitySchema.parse({
            id: crypto.randomUUID(),
            type,
            title,
            createdBy,
            createdAt: new Date(),
            communicationType,
            ...options,
        });
    }
    static createDocumentActivity(type, title, createdBy, documentId, documentName, documentType, options) {
        return DocumentActivitySchema.parse({
            id: crypto.randomUUID(),
            type,
            title,
            createdBy,
            createdAt: new Date(),
            documentId,
            documentName,
            documentType,
            ...options,
        });
    }
}
// Activity Helpers
export class ActivityHelpers {
    static getActivityIcon(type) {
        const iconMap = {
            // Lead activities
            [ActivityType.LEAD_CREATED]: '👤',
            [ActivityType.LEAD_UPDATED]: '✏️',
            [ActivityType.LEAD_STATUS_CHANGED]: '🔄',
            [ActivityType.LEAD_ASSIGNED]: '👥',
            [ActivityType.LEAD_UNASSIGNED]: '👤',
            [ActivityType.LEAD_DELETED]: '🗑️',
            // Task activities
            [ActivityType.TASK_CREATED]: '📝',
            [ActivityType.TASK_UPDATED]: '✏️',
            [ActivityType.TASK_STARTED]: '▶️',
            [ActivityType.TASK_COMPLETED]: '✅',
            [ActivityType.TASK_CANCELLED]: '❌',
            [ActivityType.TASK_ASSIGNED]: '👥',
            [ActivityType.TASK_STATUS_CHANGED]: '🔄',
            [ActivityType.TASK_PRIORITY_CHANGED]: '⚡',
            [ActivityType.TASK_DUE_DATE_CHANGED]: '📅',
            // Communication activities
            [ActivityType.CALL_MADE]: '📞',
            [ActivityType.CALL_RECEIVED]: '📞',
            [ActivityType.EMAIL_SENT]: '📧',
            [ActivityType.EMAIL_RECEIVED]: '📧',
            [ActivityType.SMS_SENT]: '💬',
            [ActivityType.WHATSAPP_SENT]: '💬',
            // Meeting activities
            [ActivityType.MEETING_SCHEDULED]: '📅',
            [ActivityType.MEETING_COMPLETED]: '✅',
            [ActivityType.MEETING_CANCELLED]: '❌',
            [ActivityType.MEETING_RESCHEDULED]: '🔄',
            // Document activities
            [ActivityType.DOCUMENT_UPLOADED]: '📎',
            [ActivityType.DOCUMENT_DOWNLOADED]: '⬇️',
            [ActivityType.DOCUMENT_SHARED]: '🔗',
            // Note activities
            [ActivityType.NOTE_ADDED]: '📝',
            [ActivityType.NOTE_UPDATED]: '✏️',
            [ActivityType.NOTE_DELETED]: '🗑️',
            // Follow-up activities
            [ActivityType.FOLLOW_UP_SCHEDULED]: '⏰',
            [ActivityType.FOLLOW_UP_COMPLETED]: '✅',
            [ActivityType.REMINDER_SET]: '🔔',
            // System activities
            [ActivityType.BULK_IMPORT]: '📥',
            [ActivityType.EXPORT_GENERATED]: '📤',
            [ActivityType.INTEGRATION_SYNC]: '🔄',
        };
        return iconMap[type] || '📋';
    }
    static getActivityColor(type) {
        if (type.includes('CREATED'))
            return '#10B981'; // Green
        if (type.includes('UPDATED') || type.includes('CHANGED'))
            return '#F59E0B'; // Yellow
        if (type.includes('COMPLETED'))
            return '#22C55E'; // Green
        if (type.includes('CANCELLED') || type.includes('DELETED'))
            return '#EF4444'; // Red
        if (type.includes('CALL') || type.includes('EMAIL') || type.includes('SMS'))
            return '#3B82F6'; // Blue
        if (type.includes('MEETING'))
            return '#8B5CF6'; // Purple
        if (type.includes('DOCUMENT'))
            return '#06B6D4'; // Cyan
        if (type.includes('TASK'))
            return '#F97316'; // Orange
        return '#6B7280'; // Gray (default)
    }
    static formatActivityTitle(activity, userNames) {
        const userName = userNames?.[activity.createdBy] || 'Unknown User';
        switch (activity.type) {
            case ActivityType.LEAD_CREATED:
                return `${userName} created this lead`;
            case ActivityType.LEAD_STATUS_CHANGED:
                return `${userName} changed status from "${activity.oldValue}" to "${activity.newValue}"`;
            case ActivityType.TASK_CREATED:
                return `${userName} created task: ${activity.title}`;
            case ActivityType.CALL_MADE:
                return `${userName} made a call`;
            case ActivityType.MEETING_SCHEDULED:
                return `${userName} scheduled a meeting`;
            default:
                return activity.title;
        }
    }
    static groupActivitiesByDate(activities) {
        return activities.reduce((groups, activity) => {
            const date = activity.createdAt.toISOString().split('T')[0];
            if (date) {
                if (!groups[date]) {
                    groups[date] = [];
                }
                groups[date].push(activity);
            }
            return groups;
        }, {});
    }
    static getActivityStats(activities) {
        const stats = {
            totalActivities: activities.length,
            activitiesByType: {},
            activitiesByUser: {},
            recentActivity: activities.length > 0 ? activities[0] : null,
        };
        activities.forEach(activity => {
            // Count by type
            stats.activitiesByType[activity.type] = (stats.activitiesByType[activity.type] || 0) + 1;
            // Count by user
            stats.activitiesByUser[activity.createdBy] = (stats.activitiesByUser[activity.createdBy] || 0) + 1;
        });
        return stats;
    }
}
export class ActivityTimeline {
    static createTimelineItem(activity, user) {
        return {
            id: activity.id,
            type: activity.type,
            title: ActivityHelpers.formatActivityTitle(activity, { [user.id]: user.name }),
            description: activity.description,
            icon: ActivityHelpers.getActivityIcon(activity.type),
            color: ActivityHelpers.getActivityColor(activity.type),
            timestamp: activity.createdAt,
            user,
            metadata: activity.metadata,
            relatedItems: {
                leadId: activity.leadId,
                taskId: activity.taskId,
            },
        };
    }
    static sortByTimestamp(items, descending = true) {
        return items.sort((a, b) => {
            const comparison = a.timestamp.getTime() - b.timestamp.getTime();
            return descending ? -comparison : comparison;
        });
    }
    static filterByDateRange(items, startDate, endDate) {
        return items.filter(item => item.timestamp >= startDate && item.timestamp <= endDate);
    }
    static filterByTypes(items, types) {
        return items.filter(item => types.includes(item.type));
    }
}
//# sourceMappingURL=Activity.js.map